/**
 * LOP — B40 strikethrough sync.
 *
 * Some products in the SALE PLP get their discount only at runtime via the third-party B40
 * pricing app (it injects a `.b40-vprice` element with the discounted amount, OR rewrites
 * the price text in place). When B40 does not also add a strikethrough with the original
 * price, the card looks inconsistent next to cards where the theme rendered a Shopify-level
 * compare_at_price.
 *
 * Strategy (selector-agnostic):
 *   1. For each `.product-item`, read `data-lop-listing-price-cents` (theme price for the
 *      currently displayed variant — written by Liquid, not affected by B40).
 *   2. Scan every <span> inside `.product-item__price` (excluding visually-hidden labels and
 *      any <s> we already inserted).
 *   3. Parse each span's text as a price. If any visible price is meaningfully lower than the
 *      theme price (≥ 1% off), treat the lowest as the B40-injected discount.
 *   4. Insert <s class="t-subdued lop-b40-injected-strikethrough"> with the theme price BEFORE
 *      the discounted span, and add `.sale` to that span.
 *
 * If theme already has a real <s> with text (Shopify compare_at_price was set), do nothing.
 *
 * Debug:
 *   window.LOP_DEBUG_B40 = true;   // before reload — logs each card decision
 *   LOP_SYNC_B40_STRIKETHROUGH();  // force a pass right now
 *
 * Loaded from layout/theme.liquid.
 */
(function () {
  'use strict';

  var INJECTED_CLASS = 'lop-b40-injected-strikethrough';
  // Spans we should NEVER consider as candidate prices, even if they hold money-shaped text.
  var IGNORE_SELECTOR = 'script, style, .visually-hidden, .product-item__price s, .lop-b40-injected-strikethrough, .product-discount-percent-badge, .displayed-discount, .unit-price, .unit-price__separator, .unit-price__price';
  // Minimum gap between B40 price and theme price to consider it a real discount (avoid rounding noise).
  var MIN_GAP_RATIO = 0.01; // 1%

  function dlog() {
    if (typeof window === 'undefined' || !window.LOP_DEBUG_B40) return;
    try {
      var args = ['[lop-b40-sync]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    } catch (e) {}
  }

  function parsePriceToCents(text) {
    if (!text) return NaN;
    var cleaned = String(text).replace(/[^\d.,]/g, '').trim();
    if (!cleaned) return NaN;

    var integer = cleaned;
    var decimal = '00';
    var hasDot = cleaned.indexOf('.') >= 0;
    var hasComma = cleaned.indexOf(',') >= 0;

    if (hasDot && hasComma) {
      var lastDot = cleaned.lastIndexOf('.');
      var lastComma = cleaned.lastIndexOf(',');
      var sepPos = Math.max(lastDot, lastComma);
      integer = cleaned.slice(0, sepPos).replace(/[.,]/g, '');
      decimal = cleaned.slice(sepPos + 1);
    } else if (hasDot) {
      var dotPos = cleaned.lastIndexOf('.');
      var afterDot = cleaned.length - dotPos - 1;
      if (afterDot >= 1 && afterDot <= 2) {
        integer = cleaned.slice(0, dotPos);
        decimal = cleaned.slice(dotPos + 1);
      } else {
        integer = cleaned.replace(/\./g, '');
      }
    } else if (hasComma) {
      var commaPos = cleaned.lastIndexOf(',');
      var afterComma = cleaned.length - commaPos - 1;
      if (afterComma >= 1 && afterComma <= 2) {
        integer = cleaned.slice(0, commaPos);
        decimal = cleaned.slice(commaPos + 1);
      } else {
        integer = cleaned.replace(/,/g, '');
      }
    }

    while (decimal.length < 2) decimal += '0';
    if (decimal.length > 2) decimal = decimal.slice(0, 2);

    var n = parseInt(integer, 10) * 100 + parseInt(decimal, 10);
    return isNaN(n) ? NaN : n;
  }

  function formatCentsLikeSample(cents, sampleText) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    var prefixMatch = String(sampleText).match(/^(\D+)/);
    var prefix = prefixMatch ? prefixMatch[1] : '$';

    var decSep = '.';
    var lastDot = String(sampleText).lastIndexOf('.');
    var lastComma = String(sampleText).lastIndexOf(',');
    if (lastComma >= 0 && lastComma > lastDot) decSep = ',';
    var thouSep = decSep === '.' ? ',' : '.';

    var dollars = Math.floor(cents / 100);
    var rem = cents % 100;
    var dollarsStr = String(dollars).replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    var centsStr = rem < 10 ? '0' + rem : String(rem);

    return prefix + dollarsStr + decSep + centsStr;
  }

  function findExistingThemeStrikethrough(priceEl) {
    if (!priceEl) return null;
    var nodes = priceEl.querySelectorAll('s');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.classList.contains(INJECTED_CLASS)) continue;
      if (n.textContent && n.textContent.trim().length > 0) return n;
    }
    return null;
  }

  function findOurInjectedStrikethrough(priceEl) {
    if (!priceEl) return null;
    return priceEl.querySelector('s.' + INJECTED_CLASS);
  }

  /**
   * Find the discounted price span in a product price element.
   * Returns { el, cents } of the lowest visible monetary span that is meaningfully below themePriceCents.
   */
  function findDiscountedPriceSpan(priceEl, themePriceCents) {
    if (!priceEl) return null;
    var spans = priceEl.querySelectorAll('span, div');
    var best = null;
    for (var i = 0; i < spans.length; i++) {
      var el = spans[i];
      if (el.matches && el.matches(IGNORE_SELECTOR)) continue;
      // Skip if the element is inside an excluded ancestor.
      if (el.closest && el.closest('.visually-hidden, s.' + INJECTED_CLASS + ', .unit-price, .product-discount-percent-badge, .displayed-discount')) continue;
      // We want spans whose direct text is the price (avoid containers with multiple spans inside).
      // Use textContent but ignore if it has child elements that are themselves price candidates.
      var text = (el.textContent || '').trim();
      if (!text) continue;
      // Quick reject: must contain a digit.
      if (!/\d/.test(text)) continue;
      var cents = parsePriceToCents(text);
      if (isNaN(cents) || cents <= 0) continue;
      // Ignore if it equals theme price (no discount on this span).
      if (cents >= themePriceCents) continue;
      // Require meaningful gap.
      var gap = (themePriceCents - cents) / themePriceCents;
      if (gap < MIN_GAP_RATIO) continue;
      // Prefer leaf-most spans (no element children that also match a digit).
      var hasChildPrice = false;
      var children = el.children;
      for (var c = 0; c < children.length; c++) {
        var ct = (children[c].textContent || '').trim();
        if (ct && /\d/.test(ct) && parsePriceToCents(ct) === cents) {
          hasChildPrice = true; break;
        }
      }
      if (hasChildPrice) continue;

      if (!best || cents < best.cents) {
        best = { el: el, cents: cents, text: text };
      }
    }
    return best;
  }

  function syncItem(item) {
    var priceEl = item.querySelector('.product-item__price');
    if (!priceEl) {
      dlog('skip: no .product-item__price', item);
      return;
    }

    if (findExistingThemeStrikethrough(priceEl)) {
      var stale = findOurInjectedStrikethrough(priceEl);
      if (stale) stale.parentNode.removeChild(stale);
      dlog('skip: theme already shows real <s>', item.getAttribute('data-current-variant-id'));
      return;
    }

    var themePriceCents = parseInt(item.getAttribute('data-lop-listing-price-cents'), 10);
    if (!themePriceCents || isNaN(themePriceCents)) {
      dlog('skip: missing data-lop-listing-price-cents', item);
      return;
    }

    var found = findDiscountedPriceSpan(priceEl, themePriceCents);
    var ours = findOurInjectedStrikethrough(priceEl);

    if (!found) {
      if (ours) ours.parentNode.removeChild(ours);
      dlog('no discounted span found', { themePriceCents: themePriceCents, html: priceEl.innerHTML });
      return;
    }

    var formatted = formatCentsLikeSample(themePriceCents, found.text);

    if (!ours) {
      ours = document.createElement('s');
      ours.className = 't-subdued ' + INJECTED_CLASS;
      found.el.parentNode.insertBefore(ours, found.el);
    }
    if (ours.textContent !== formatted) {
      ours.textContent = formatted;
    }

    if (!found.el.classList.contains('sale')) {
      found.el.classList.add('sale');
    }

    dlog('injected', {
      variantId: item.getAttribute('data-current-variant-id'),
      themePriceCents: themePriceCents,
      b40Cents: found.cents,
      original: formatted,
      sample: found.text,
    });
  }

  function syncAll() {
    var items = document.querySelectorAll('.product-item');
    for (var i = 0; i < items.length; i++) syncItem(items[i]);
  }

  var pending = null;
  function scheduleSync() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      syncAll();
    }, 100);
  }

  function runImmediatePasses() {
    syncAll();
    var rafCount = 0;
    function rafLoop() {
      syncAll();
      rafCount++;
      if (rafCount < 6 && window.requestAnimationFrame) {
        window.requestAnimationFrame(rafLoop);
      }
    }
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(rafLoop);
    }
  }

  function init() {
    runImmediatePasses();
    // B40 may inject late; retry several times for a few seconds (spread out to avoid stampede).
    var retryDelays = [150, 350, 600, 1200, 2200, 4000];
    retryDelays.forEach(function (ms) { setTimeout(syncAll, ms); });

    if (window.MutationObserver) {
      var obs = new MutationObserver(function (mutations) {
        var relevant = false;
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type === 'characterData') {
            var p = m.target && m.target.parentElement;
            if (p && p.closest && p.closest('.product-item__price')) {
              relevant = true; break;
            }
            continue;
          }
          // childList: any added node inside or near a product-item or price element.
          var added = m.addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.matches && (n.matches('.product-item') || n.closest && n.closest('.product-item__price'))) {
              relevant = true; break;
            }
            if (n.querySelector && n.querySelector('.product-item, .product-item__price')) {
              relevant = true; break;
            }
          }
          if (relevant) break;
          // Also catch attribute changes on product-item (e.g. data-lop-listing-price-cents updated by swatch)
          if (m.type === 'attributes' && m.target && m.target.matches && m.target.matches('.product-item')) {
            relevant = true; break;
          }
        }
        if (relevant) scheduleSync();
      });
      obs.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-lop-listing-price-cents', 'data-lop-listing-compare-at-cents', 'data-current-variant-id'],
      });
    }

    document.addEventListener('shopify:section:load', scheduleSync);
  }

  function boot() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  boot();

  window.LOP_SYNC_B40_STRIKETHROUGH = syncAll;
})();
